import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Context } from "../../models/context";
import {
    CampaignI,
    SubscriberI,
    GetCampaigns,
    PostCampaign,
    PutCampaign,
    DeleteCampaign,
    GetSubscribers,
    PostSubscriber,
    DeleteSubscriber,
} from "../../services/campaigns";
import { GetProjects } from "../../services/projects";
import { Project } from "../../models/project";
import Modal from "../../components/modal/Modal";
import { useAlerts } from "../../contexts/AlertContext";
import "./campaign.css";

const DIFFICULTY_OPTIONS = ["easy", "medium", "hard"] as const;

const DIFFICULTY_COLOR: Record<string, string> = {
    easy: "campaign-diff-easy",
    medium: "campaign-diff-medium",
    hard: "campaign-diff-hard",
};

/** Blank campaign used to reset the create form. */
const BLANK_CAMPAIGN: CampaignI = { campaign: "", difficulty: "easy", primaryProject: undefined, backupProjects: [] };

/** Returns a fresh mutable copy of a campaign for editing. */
function cloneCampaign(c: CampaignI): CampaignI {
    return { ...c, backupProjects: [...(c.backupProjects ?? [])] };
}

export default function CampaignPage() {
    const context = useContext(Context);
    const navigate = useNavigate();
    const { addError, addAlert } = useAlerts();

    const [campaigns, setCampaigns] = useState<CampaignI[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);

    // Create form state
    const [createForm, setCreateForm] = useState<CampaignI>(BLANK_CAMPAIGN);
    const [createSubmitting, setCreateSubmitting] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);

    // Expanded/editing campaigns
    const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null);
    const [editDraft, setEditDraft] = useState<CampaignI | null>(null);
    const [editSaving, setEditSaving] = useState(false);
    const [deletingCampaign, setDeletingCampaign] = useState<string | null>(null);

    // Subscriber modal
    const [subscriberModal, setSubscriberModal] = useState<string | null>(null);
    const [subscribers, setSubscribers] = useState<SubscriberI[]>([]);
    const [subscribersLoading, setSubscribersLoading] = useState(false);
    const [subscriberForm, setSubscriberForm] = useState<SubscriberI>({ email: "", name: "" });
    const [subscriberEditTarget, setSubscriberEditTarget] = useState<string | null>(null);
    const [subscriberFormSaving, setSubscriberFormSaving] = useState(false);

    useEffect(() => {
        if (!context.user) { navigate("/login"); return; }
        fetchCampaigns();
        fetchProjects();
    }, [context.user, navigate]);

    async function fetchCampaigns() {
        if (!context.user) return;
        try {
            const result = await new GetCampaigns().request(context.user);
            setCampaigns(result);
        } catch (error) {
            addError(error as Error, "Error loading campaigns");
        }
    }

    async function fetchProjects() {
        if (!context.user) return;
        try {
            const result = await new GetProjects().request(context.user);
            setProjects(result);
        } catch (error) {
            addError(error as Error, "Error loading projects");
        }
    }

    // ── Create campaign ──────────────────────────────────────────────────────

    function toggleCreateBackup(name: string) {
        setCreateForm(prev => {
            const next = new Set(prev.backupProjects ?? []);
            if (next.has(name)) next.delete(name); else next.add(name);
            return { ...prev, backupProjects: Array.from(next) };
        });
    }

    async function handleCreate(e: React.FormEvent) {
        e.preventDefault();
        if (!context.user) return;
        setCreateSubmitting(true);
        try {
            await new PostCampaign().request(createForm, context.user);
            addAlert(`Campaign "${createForm.campaign}" created.`, 5000, "success", true);
            setCreateForm(BLANK_CAMPAIGN);
            fetchCampaigns();
        } catch (error) {
            addError(error as Error, "Failed to create campaign");
        } finally {
            setCreateSubmitting(false);
        }
    }

    // ── Expand / collapse ────────────────────────────────────────────────────

    function handleExpand(c: CampaignI) {
        if (expandedCampaign === c.campaign) {
            setExpandedCampaign(null);
            setEditDraft(null);
        } else {
            setExpandedCampaign(c.campaign);
            setEditDraft(cloneCampaign(c));
        }
    }

    // ── Edit campaign ────────────────────────────────────────────────────────

    function toggleEditBackup(name: string) {
        if (!editDraft) return;
        const next = new Set(editDraft.backupProjects ?? []);
        if (next.has(name)) next.delete(name); else next.add(name);
        setEditDraft({ ...editDraft, backupProjects: Array.from(next) });
    }

    async function handleSaveEdit() {
        if (!context.user || !editDraft) return;
        setEditSaving(true);
        try {
            await new PutCampaign().request(editDraft, context.user);
            addAlert(`Campaign "${editDraft.campaign}" updated.`, 4000, "success", true);
            fetchCampaigns();
        } catch (error) {
            addError(error as Error, "Failed to update campaign");
        } finally {
            setEditSaving(false);
        }
    }

    // ── Delete campaign ──────────────────────────────────────────────────────

    async function handleDelete(campaignName: string) {
        if (!context.user) return;
        setDeletingCampaign(campaignName);
        try {
            await new DeleteCampaign(campaignName).request(context.user);
            addAlert(`Campaign "${campaignName}" deleted.`, 4000, "success", true);
            if (expandedCampaign === campaignName) setExpandedCampaign(null);
            fetchCampaigns();
        } catch (error) {
            addError(error as Error, "Failed to delete campaign");
        } finally {
            setDeletingCampaign(null);
        }
    }

    // ── Subscriber modal ─────────────────────────────────────────────────────

    async function openSubscriberModal(campaignName: string) {
        setSubscriberModal(campaignName);
        setSubscriberForm({ email: "", name: "" });
        setSubscriberEditTarget(null);
        setSubscribersLoading(true);
        try {
            const result = await new GetSubscribers(campaignName).request(context.user!);
            setSubscribers(result);
        } catch (error) {
            addError(error as Error, "Failed to load subscribers");
            setSubscribers([]);
        } finally {
            setSubscribersLoading(false);
        }
    }

    function closeSubscriberModal() {
        setSubscriberModal(null);
        setSubscribers([]);
        setSubscriberForm({ email: "", name: "" });
        setSubscriberEditTarget(null);
    }

    function startEditSubscriber(s: SubscriberI) {
        setSubscriberEditTarget(s.email);
        setSubscriberForm({ email: s.email, name: s.name });
    }

    function cancelEditSubscriber() {
        setSubscriberEditTarget(null);
        setSubscriberForm({ email: "", name: "" });
    }

    async function handleSaveSubscriber(e: React.FormEvent) {
        e.preventDefault();
        if (!context.user || !subscriberModal) return;
        setSubscriberFormSaving(true);
        try {
            await new PostSubscriber(subscriberModal).request(subscriberForm, context.user);
            const verb = subscriberEditTarget ? "updated" : "added";
            addAlert(`Subscriber "${subscriberForm.email}" ${verb}.`, 4000, "success", true);
            setSubscriberForm({ email: "", name: "" });
            setSubscriberEditTarget(null);
            const result = await new GetSubscribers(subscriberModal).request(context.user);
            setSubscribers(result);
        } catch (error) {
            addError(error as Error, "Failed to save subscriber");
        } finally {
            setSubscriberFormSaving(false);
        }
    }

    async function handleDeleteSubscriber(email: string) {
        if (!context.user || !subscriberModal) return;
        try {
            await new DeleteSubscriber(subscriberModal, email).request(context.user);
            addAlert(`Subscriber "${email}" removed.`, 4000, "success", true);
            setSubscribers(prev => prev.filter(s => s.email !== email));
            if (subscriberEditTarget === email) cancelEditSubscriber();
        } catch (error) {
            addError(error as Error, "Failed to remove subscriber");
        }
    }

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div id="campaign-page">
            <h1>Email Campaigns</h1>

            {/* ── Campaign List ───────────────────────────────────────────── */}
            <div id="campaign-list-wrapper" className="container">
                <h2>All Campaigns</h2>
                {campaigns.length === 0 && <p className="campaign-empty">No campaigns yet.</p>}
                <ul id="campaign-list">
                    {campaigns.map(c => {
                        const isExpanded = expandedCampaign === c.campaign;
                        const isDeleting = deletingCampaign === c.campaign;
                        return (
                            <li key={c.campaign} className={`campaign-card${isExpanded ? " campaign-card--expanded" : ""}`}>

                                {/* Card header – click to expand */}
                                <div className="campaign-card-header" onClick={() => handleExpand(c)} role="button" aria-expanded={isExpanded}>
                                    <div className="campaign-card-header-left">
                                        <span className="campaign-chevron" aria-hidden>{isExpanded ? "▾" : "▸"}</span>
                                        <strong className="campaign-card-title">{c.campaign}</strong>
                                    </div>
                                    <div className="campaign-card-header-right">
                                        <span className={`campaign-difficulty-badge ${DIFFICULTY_COLOR[c.difficulty] ?? ""}`}>
                                            {c.difficulty}
                                        </span>
                                    </div>
                                </div>

                                {/* Expanded details / edit */}
                                {isExpanded && editDraft && (
                                    <div className="campaign-card-body">
                                        <div className="campaign-edit-grid">

                                            <div className="campaign-edit-section">
                                                <span className="campaign-edit-label">Primary Project</span>
                                                <div className="campaign-option-group">
                                                    <label className="campaign-option-label">
                                                        <input
                                                            type="radio"
                                                            name={`${c.campaign}-primary`}
                                                            value=""
                                                            checked={!editDraft.primaryProject}
                                                            onChange={() => setEditDraft({ ...editDraft, primaryProject: undefined })}
                                                        />
                                                        <em>None</em>
                                                    </label>
                                                    {projects.map(p => (
                                                        <label key={p.name} className="campaign-option-label">
                                                            <input
                                                                type="radio"
                                                                name={`${c.campaign}-primary`}
                                                                value={p.name}
                                                                checked={editDraft.primaryProject === p.name}
                                                                onChange={() => setEditDraft({ ...editDraft, primaryProject: p.name })}
                                                            />
                                                            {p.name}
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="campaign-edit-section">
                                                <span className="campaign-edit-label">Backup Projects</span>
                                                <div className="campaign-option-group">
                                                    {projects.map(p => (
                                                        <label key={p.name} className="campaign-option-label">
                                                            <input
                                                                type="checkbox"
                                                                checked={(editDraft.backupProjects ?? []).includes(p.name)}
                                                                onChange={() => toggleEditBackup(p.name)}
                                                            />
                                                            {p.name}
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="campaign-edit-section">
                                                <span className="campaign-edit-label">Difficulty</span>
                                                <div className="campaign-option-group">
                                                    {DIFFICULTY_OPTIONS.map(d => (
                                                        <label key={d} className="campaign-option-label">
                                                            <input
                                                                type="radio"
                                                                name={`${c.campaign}-difficulty`}
                                                                value={d}
                                                                checked={editDraft.difficulty === d}
                                                                onChange={() => setEditDraft({ ...editDraft, difficulty: d })}
                                                            />
                                                            {d.charAt(0).toUpperCase() + d.slice(1)}
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                        </div>

                                        <div className="campaign-card-actions">
                                            <button
                                                type="button"
                                                className="campaign-subscribers-btn"
                                                onClick={() => openSubscriberModal(c.campaign)}
                                                id={`subscribers-btn-${c.campaign}`}
                                            >
                                                Subscribers
                                            </button>
                                            <button
                                                type="button"
                                                className="campaign-save-btn"
                                                onClick={handleSaveEdit}
                                                disabled={editSaving}
                                                id={`save-btn-${c.campaign}`}
                                            >
                                                {editSaving ? "Saving…" : "Save Changes"}
                                            </button>
                                            <button
                                                type="button"
                                                className="campaign-delete-btn"
                                                onClick={() => handleDelete(c.campaign)}
                                                disabled={isDeleting}
                                                id={`delete-btn-${c.campaign}`}
                                            >
                                                {isDeleting ? "Deleting…" : "Delete"}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>

            {/* ── Create Campaign Form ────────────────────────────────────── */}
            <div id="campaign-create-wrapper" className="container">
                <div
                    id="campaign-create-header"
                    onClick={() => setCreateOpen(o => !o)}
                    role="button"
                    aria-expanded={createOpen}
                >
                    <span className="campaign-chevron" aria-hidden>{createOpen ? "▾" : "▸"}</span>
                    <h2>Create Campaign</h2>
                </div>
                {createOpen && (
                    <form id="campaign-form" onSubmit={handleCreate}>
                        <ul id="campaign-form-body">

                            <li>
                                <label htmlFor="campaign-name">Campaign Name:</label>
                                <input
                                    type="text"
                                    id="campaign-name"
                                    placeholder="Campaign name"
                                    value={createForm.campaign}
                                    onChange={e => setCreateForm(prev => ({ ...prev, campaign: e.target.value }))}
                                    required
                                    maxLength={50}
                                />
                            </li>

                            <li className="campaign-field-section">
                                <label>Primary Project:</label>
                                <div className="campaign-option-group">
                                    {projects.map(p => (
                                        <label key={p.name} className="campaign-option-label">
                                            <input
                                                type="radio"
                                                name="create-primaryProject"
                                                value={p.name}
                                                checked={createForm.primaryProject === p.name}
                                                onChange={() => setCreateForm(prev => ({ ...prev, primaryProject: p.name }))}
                                            />
                                            {p.name}
                                        </label>
                                    ))}
                                </div>
                            </li>

                            <li className="campaign-field-section">
                                <label>Backup Projects:</label>
                                <div className="campaign-option-group">
                                    {projects.map(p => (
                                        <label key={p.name} className="campaign-option-label">
                                            <input
                                                type="checkbox"
                                                value={p.name}
                                                checked={(createForm.backupProjects ?? []).includes(p.name)}
                                                onChange={() => toggleCreateBackup(p.name)}
                                            />
                                            {p.name}
                                        </label>
                                    ))}
                                </div>
                            </li>

                            <li className="campaign-field-section">
                                <label>Difficulty:</label>
                                <div className="campaign-option-group">
                                    {DIFFICULTY_OPTIONS.map(d => (
                                        <label key={d} className="campaign-option-label">
                                            <input
                                                type="radio"
                                                name="create-difficulty"
                                                value={d}
                                                checked={createForm.difficulty === d}
                                                onChange={() => setCreateForm(prev => ({ ...prev, difficulty: d }))}
                                                required
                                            />
                                            {d.charAt(0).toUpperCase() + d.slice(1)}
                                        </label>
                                    ))}
                                </div>
                            </li>

                        </ul>
                        <input
                            type="submit"
                            id="submit-button"
                            className="button"
                            value={createSubmitting ? "Creating…" : "Create Campaign"}
                            disabled={createSubmitting}
                        />
                    </form>
                )}
            </div>

            {/* ── Subscriber Modal ────────────────────────────────────────── */}
            <Modal
                isOpen={subscriberModal !== null}
                onClose={closeSubscriberModal}
                title={`Subscribers — ${subscriberModal}`}
                closeOnOverlayClick={true}
            >
                <div id="subscriber-modal-body">

                    {/* Add / Edit subscriber form */}
                    <form id="subscriber-form" onSubmit={handleSaveSubscriber}>
                        <h3 className="subscriber-form-title">
                            {subscriberEditTarget ? "Edit Subscriber" : "Add Subscriber"}
                        </h3>
                        <div className="subscriber-form-fields">
                            <input
                                type="email"
                                id="subscriber-email"
                                placeholder="Email"
                                value={subscriberForm.email}
                                onChange={e => setSubscriberForm(prev => ({ ...prev, email: e.target.value }))}
                                required
                                readOnly={!!subscriberEditTarget}
                                className={subscriberEditTarget ? "subscriber-email-readonly" : ""}
                            />
                            <input
                                type="text"
                                id="subscriber-name"
                                placeholder="Name"
                                value={subscriberForm.name}
                                onChange={e => setSubscriberForm(prev => ({ ...prev, name: e.target.value }))}
                                required
                            />
                        </div>
                        <div className="subscriber-form-actions">
                            <input
                                type="submit"
                                className="button campaign-save-btn"
                                value={subscriberFormSaving ? "Saving…" : (subscriberEditTarget ? "Update" : "Add")}
                                disabled={subscriberFormSaving}
                                id="subscriber-save-btn"
                            />
                            {subscriberEditTarget && (
                                <button type="button" className="campaign-edit-btn" onClick={cancelEditSubscriber}>
                                    Cancel
                                </button>
                            )}
                        </div>
                    </form>

                    <hr className="subscriber-divider" />

                    {/* Subscriber list */}
                    {subscribersLoading && <p className="campaign-loading">Loading subscribers…</p>}
                    {!subscribersLoading && subscribers.length === 0 && (
                        <p className="campaign-empty">No subscribers yet.</p>
                    )}
                    {!subscribersLoading && subscribers.length > 0 && (
                        <ul id="subscriber-list">
                            {subscribers.map(s => (
                                <li key={s.email} className={`subscriber-row${subscriberEditTarget === s.email ? " subscriber-row--editing" : ""}`}>
                                    <div className="subscriber-info">
                                        <span className="subscriber-name">{s.name}</span>
                                        <span className="subscriber-email">{s.email}</span>
                                    </div>
                                    <div className="subscriber-actions">
                                        <button
                                            type="button"
                                            className="campaign-edit-btn"
                                            onClick={() => startEditSubscriber(s)}
                                            id={`edit-subscriber-${s.email}`}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            type="button"
                                            className="campaign-delete-btn"
                                            onClick={() => handleDeleteSubscriber(s.email)}
                                            id={`delete-subscriber-${s.email}`}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </Modal>
        </div>
    );
}
